import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FiMenu, FiChevronDown, FiPlus, FiAlertTriangle } from 'react-icons/fi';
import { get } from './httpClient';
import { PropertyTypeIcon } from './propertyTypeIcons';

const DEFAULT_PROP = { name: '', type: 'text', required: false, show_name: true, as_title: false, as_subtitle: false, meta: null, alignment: 'left' };
const DEFAULT_COLUMN = { components: [], width: 100, alignment: 'top' };
const MAX_COLUMNS = 3;
const HORIZONTAL_ALIGNMENTS = ['left', 'center', 'right'];
const VERTICAL_ALIGNMENTS = ['top', 'center', 'bottom'];

function normalizeMobileProps(rawProps) {
  if (!Array.isArray(rawProps)) return [];
  return rawProps.map((mp) => ({
    row_idx:    typeof mp?.row_idx    === 'number' ? mp.row_idx    : 0,
    column_idx: typeof mp?.column_idx === 'number' ? mp.column_idx : 0,
    component_idx:   typeof mp?.component_idx   === 'number' ? mp.component_idx   : 0,
    visible:    mp?.visible == null ? true : !!mp.visible,
  }));
}

// Keeps mobile.components in sync with current rows/columns.
// Removes stale refs, appends newly added props (visible=true).
export function syncMobileProps(rows, currentMobileProps) {
  const validKeys = new Set();
  for (let rIdx = 0; rIdx < rows.length; rIdx++) {
    const columns = rows[rIdx]?.columns || [];
    for (let cIdx = 0; cIdx < columns.length; cIdx++) {
      for (let pIdx = 0; pIdx < (columns[cIdx].components || []).length; pIdx++) {
        validKeys.add(`${rIdx}:${cIdx}:${pIdx}`);
      }
    }
  }
  const kept = currentMobileProps.filter((mp) => validKeys.has(`${mp.row_idx}:${mp.column_idx}:${mp.component_idx}`));
  const keptKeys = new Set(kept.map((mp) => `${mp.row_idx}:${mp.column_idx}:${mp.component_idx}`));
  const added = [];
  for (let rIdx = 0; rIdx < rows.length; rIdx++) {
    const columns = rows[rIdx]?.columns || [];
    for (let cIdx = 0; cIdx < columns.length; cIdx++) {
      for (let pIdx = 0; pIdx < (columns[cIdx].components || []).length; pIdx++) {
        if (!keptKeys.has(`${rIdx}:${cIdx}:${pIdx}`)) {
          added.push({ row_idx: rIdx, column_idx: cIdx, component_idx: pIdx, visible: true });
        }
      }
    }
  }
  return [...kept, ...added];
}

const ALLOWED_CUTS = ['none', 'small', 'medium'];

// Settings for a gallery-type layout property (multi-image carousel).
// behaves the same way as an embedded gallery component.
const DEFAULT_GALLERY_META = {
  autoPlay: true,
  showNav: false,
  showFullScreenButton: true,
  showThumbnails: false,
  showImageTitles: true,
  slideIntervalSeconds: 7,
};

function normalizeGalleryMeta(m) {
  const src = (m && typeof m === 'object') ? m : {};
  return {
    autoPlay: src.autoPlay !== undefined ? !!src.autoPlay : DEFAULT_GALLERY_META.autoPlay,
    showNav: src.showNav !== undefined ? !!src.showNav : DEFAULT_GALLERY_META.showNav,
    showFullScreenButton: src.showFullScreenButton !== undefined ? !!src.showFullScreenButton : DEFAULT_GALLERY_META.showFullScreenButton,
    showThumbnails: src.showThumbnails !== undefined ? !!src.showThumbnails : DEFAULT_GALLERY_META.showThumbnails,
    showImageTitles: src.showImageTitles !== undefined ? !!src.showImageTitles : DEFAULT_GALLERY_META.showImageTitles,
    slideIntervalSeconds: Number.isFinite(Number(src.slideIntervalSeconds)) && Number(src.slideIntervalSeconds) > 0
      ? Number(src.slideIntervalSeconds) : DEFAULT_GALLERY_META.slideIntervalSeconds,
  };
}

function normalizeProperty(p) {
  const rawType = (p?.type || '').toLowerCase();
  const type = ['gallery', 'text', 'line', 'date', 'widget', 'poster', 'catalog', 'breadcrumbs'].includes(rawType) ? rawType : DEFAULT_PROP.type;

  // meta is only supported for types 'line', 'widget', 'text' and 'gallery'. For other types
  // (including 'poster' and 'catalog', whose full settings live in the per-page value —
  // mirroring the reusable Poster Component — since a catalog is just a structural slot any
  // page hosting one fills in for itself, and 'breadcrumbs', which has no settings at all —
  // it just shows this page's own ancestry trail wherever it's placed) meta MUST be null.
  let meta = null;
  if (type === 'line') {
    const m = (p && p.meta && typeof p.meta === 'object') ? p.meta : {};
    const allowedFontSizes = ['heading', 'sub-heading', 'text'];
    const fs = String(m.fontSize ?? '').toLowerCase();
    meta = {
      prefix: String(m.prefix ?? ''),
      suffix: String(m.suffix ?? ''),
      fontSize: allowedFontSizes.includes(fs) ? fs : 'text',
    };
  } else if (type === 'widget') {
    const m = (p && p.meta && typeof p.meta === 'object') ? p.meta : {};
    meta = { widgetId: m.widgetId ? String(m.widgetId) : null };
  } else if (type === 'text') {
    const m = (p && p.meta && typeof p.meta === 'object') ? p.meta : {};
    const cut = String(m.cut ?? '').toLowerCase();
    meta = { cut: ALLOWED_CUTS.includes(cut) ? cut : 'none' };
  } else if (type === 'gallery') {
    meta = normalizeGalleryMeta(p && p.meta);
  }

  return {
    name: String(p?.name ?? DEFAULT_PROP.name),
    type,
    required: p?.required !== undefined ? !!p.required : DEFAULT_PROP.required,
    show_name: p?.show_name !== undefined ? !!p.show_name : DEFAULT_PROP.show_name,
    as_title: type === 'line' ? (p?.as_title !== undefined ? !!p.as_title : DEFAULT_PROP.as_title) : false,
    as_subtitle: p?.as_subtitle !== undefined ? !!p.as_subtitle : DEFAULT_PROP.as_subtitle,
    meta,
    alignment: HORIZONTAL_ALIGNMENTS.includes(p?.alignment) ? p.alignment : DEFAULT_PROP.alignment,
  };
}

export function normalize(struct) {
  const base = struct && typeof struct === 'object' ? struct : {};
  const rowsIn = Array.isArray(base.rows) ? base.rows : [];
  const normalizedRows = rowsIn.map((row) => {
    const cols = Array.isArray(row?.columns) ? row.columns : [];
    return {
      columns: cols.map((col) => {
        const components = Array.isArray(col?.components) ? col.components : [];
        const width = Number.isFinite(Number(col?.width)) && Number(col.width) > 0 ? Number(col.width) : 100;
        const alignment = VERTICAL_ALIGNMENTS.includes(col?.alignment) ? col.alignment : DEFAULT_COLUMN.alignment;
        return {
          width,
          alignment,
          components: components.map(normalizeProperty),
        };
      }),
    };
  });

  const mobileBase = base.mobile && typeof base.mobile === 'object' ? base.mobile : {};
  const mobile = { components: normalizeMobileProps(mobileBase.components) };

  return { rows: normalizedRows, mobile };
}

function mapAllProperties(rows, fn) {
  return rows.map((row, rIdx) => ({
    columns: (row.columns || []).map((col, cIdx) => ({
      ...col,
      components: (col.components || []).map((p, pIdx) => fn(p, rIdx, cIdx, pIdx)),
    })),
  }));
}

function rowWidthTotal(row) {
  return (row.columns || []).reduce((s, c) => s + (Number(c.width) || 0), 0);
}

const GIS_STYLES = `
  /* Container */
  .gis-container {
    border-radius: 8px;
    overflow: hidden;
    margin: 12px 0;
    background: var(--card-bg, #fff);
  }

  /* Toolbar */
  .gis-toolbar {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 7px 12px;
    background: rgba(49,59,114,0.05);
    border-bottom: 1px solid rgba(49,59,114,0.1);
  }
  .gis-tabs { display: flex; gap: 3px; }
  .gis-tab-btn {
    background: rgba(49,59,114,0.07);
    border: none;
    border-radius: 5px;
    padding: 3px 11px;
    font-size: 0.75rem;
    font-weight: 700;
    letter-spacing: 0.03em;
    color: var(--navy);
    cursor: pointer;
    opacity: 0.6;
    transition: opacity 0.12s, background 0.12s;
    font-family: inherit;
  }
  .gis-tab-btn:hover:not(.gis-tab-btn--active) { opacity: 0.9; }
  .gis-tab-btn--active {
    background: linear-gradient(135deg, var(--purple) 0%, var(--navy) 100%);
    color: #fff;
    opacity: 1;
  }
  /* "+ row" — matches structure-add-child-btn */
  .gis-add-row-btn {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    background: transparent;
    border: none;
    color: var(--navy);
    padding: 3px 6px;
    font-size: 0.8em;
    font-family: inherit;
    cursor: pointer;
    opacity: 0.6;
    transition: opacity 0.15s;
    margin-left: auto;
  }
  .gis-add-row-btn:hover { opacity: 1; }

  .gis-content { padding: 10px; }
  .gis-empty-hint {
    flex: 1 1 100%;
    color: var(--green);
    font-size: 0.82rem;
    opacity: 0.55;
    text-align: center;
    padding: 16px 0;
  }

  /* Drag-handle cluster — hidden until the row/column/property it belongs to is hovered.
     Reordering is drag-and-drop only now; there's no keyboard-operable fallback, so this
     is purely a visual affordance, not a focus target. */
  .gis-reorder-cluster {
    display: inline-flex;
    align-items: center;
    gap: 1px;
    flex-shrink: 0;
    opacity: 0;
    transition: opacity 0.12s;
  }
  .gis-row:hover .gis-reorder-cluster,
  .gis-column:hover .gis-reorder-cluster,
  .gis-prop-card:hover .gis-reorder-cluster {
    opacity: 0.55;
  }
  .gis-reorder-cluster:focus-within { opacity: 1 !important; }

  /* Row — groups a set of columns. Only the left edge draws a border, thick and colored per
     row (cycling through a fixed palette), so consecutive rows are easy to tell apart without
     stacking multiple border systems on top of each other. */
  .gis-row {
    border: none;
    border-left: 5px solid rgba(49,59,114,0.5);
    border-radius: 0 7px 7px 0;
    margin-bottom: 20px;
    overflow: hidden;
    transition: opacity 0.12s, border-color 0.12s;
  }
  .gis-row--dragging { opacity: 0.35; }

  /* Cycling per-row accent colors — bright, saturated hues so they read clearly against
     the card background instead of blending into the muted base palette. */
  .gis-row--accent-0 { border-left-color: #4457d6; }
  .gis-row--accent-1 { border-left-color: #a238d1; }
  .gis-row--accent-2 { border-left-color: #14b8a6; }
  .gis-row--accent-3 { border-left-color: #e8672a; }
  .gis-row--accent-4 { border-left-color: #e0a800; }
  .gis-row--accent-5 { border-left-color: #43a047; }
  /* Declared after the accents (equal specificity) so it wins the tie-break when both apply. */
  .gis-row--over-limit { border-left-color: rgba(160,18,51,0.75); }
  .gis-row-head {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 5px 8px 5px 10px;
    background: rgba(49,59,114,0.12);
  }
  .gis-row-head > .gis-row-label { margin-right: auto; }

  /* Drag handle — purely a visual grab affordance (aria-hidden); the up/down buttons in
     the same cluster are the real keyboard/screen-reader-accessible way to reorder. */
  .gis-drag-handle {
    display: inline-flex;
    align-items: center;
    color: var(--green);
    cursor: grab;
    flex-shrink: 0;
    padding: 0 2px;
  }
  .gis-drag-handle:active { cursor: grabbing; }
  .gis-row-label {
    font-size: 0.7rem;
    font-weight: 800;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--navy);
    opacity: 0.5;
  }
  .gis-row-actions { display: flex; align-items: center; gap: 6px; }
  .gis-row-total {
    font-size: 0.7rem;
    font-weight: 700;
    color: var(--green);
    opacity: 0.6;
  }
  .gis-row-total--over { color: #a01233; opacity: 1; }
  .gis-add-col-btn {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    background: transparent;
    border: none;
    color: var(--navy);
    padding: 3px 6px;
    font-size: 0.78em;
    font-family: inherit;
    cursor: pointer;
    opacity: 0.6;
    transition: opacity 0.15s;
  }
  .gis-add-col-btn:hover:not(:disabled) { opacity: 1; }
  .gis-add-col-btn:disabled { opacity: 0.25; cursor: not-allowed; }
  .gis-row-body {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    align-items: flex-start;
    gap: 8px;
    padding: 8px;
  }

  /* Column — subtle background trough to group components, no border of its own so it
     doesn't stack another vertical line on top of the row's border and each property's
     type-colored accent. */
  .gis-column {
    position: relative;
    border-radius: 6px;
    background: rgba(49,59,114,0.045);
    transition: opacity 0.12s;
    min-width: 0;
  }
  .gis-column--dragging { opacity: 0.35; }
  /* Vertical drop indicator for horizontal reordering — absolutely positioned so it doesn't
     nudge column widths when it appears, unlike the shared (horizontal) .gis-drop-line. */
  .gis-col-drop-line {
    position: absolute;
    top: 0;
    bottom: 0;
    left: -5px;
    width: 3px;
    border-radius: 2px;
    background: var(--navy);
    opacity: 0.6;
  }
  .gis-column-head {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px;
    padding: 4px 8px 4px 10px;
    background: rgba(49,59,114,0.07);
    border-radius: 6px 6px 0 0;
  }
  .gis-column-label {
    font-size: 0.67rem;
    font-weight: 800;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--navy);
    opacity: 0.4;
    flex-shrink: 0;
  }
  .gis-column-head-actions { display: flex; align-items: center; gap: 4px; margin-left: auto; }
  .gis-width-input {
    width: 46px !important;
    text-align: right;
  }
  .gis-width-suffix {
    font-size: 0.7rem;
    color: var(--navy);
    opacity: 0.5;
  }
  .gis-column-body { padding: 0 0 6px; }

  /* Property items — flat list with hairline separators (mirrors structure-node) */
  .gis-prop-card {
    position: relative;
    cursor: default;
    transition: background 0.12s;
    border-left: 3px solid transparent;
  }
  .gis-prop-card + .gis-prop-card {
    border-top: 1px solid rgba(98,168,124,0.45);
  }
  .gis-prop-card:hover { background: rgba(49,59,114,0.04); }
  .gis-prop-card--dragging { opacity: 0.3; }
  .gis-prop-card--duplicate { border-left-color: #a01233 !important; }

  /* Left-accent strip, colored per property type — same palette as the value editor's
     .item-prop-type-badge, so a type reads the same way in both places. */
  .gis-prop-card--line      { border-left-color: rgba(98,168,124,0.55); }
  .gis-prop-card--text      { border-left-color: rgba(49,59,114,0.4); }
  .gis-prop-card--gallery   { border-left-color: rgba(70,34,85,0.4); }
  .gis-prop-card--poster    { border-left-color: rgba(160,90,44,0.55); }
  .gis-prop-card--date      { border-left-color: rgba(154,116,17,0.55); }
  .gis-prop-card--widget { border-left-color: rgba(27,120,107,0.55); }
  .gis-prop-card--catalog { border-left-color: rgba(160,50,74,0.55); }

  /* Type icon, sitting just before the type <select> */
  .gis-prop-type-wrap { display: inline-flex; align-items: center; gap: 3px; flex: 0 0 auto; }
  .gis-prop-type-icon { flex-shrink: 0; display: inline-flex; }
  .gis-prop-type-icon--line      { color: var(--green); }
  .gis-prop-type-icon--text      { color: var(--navy); }
  .gis-prop-type-icon--gallery   { color: var(--purple); }
  .gis-prop-type-icon--poster    { color: #a05a2c; }
  .gis-prop-type-icon--date      { color: #9a7411; }
  .gis-prop-type-icon--widget { color: #1b786b; }
  .gis-prop-type-icon--catalog { color: #a0324a; }

  /* Drop indicator — matches structure-node--drag-before */
  .gis-drop-line {
    height: 2px;
    background: var(--navy);
    opacity: 0.5;
    margin: 0;
  }

  /* Mobile drag handle */
  .gis-mobile-drag-handle {
    display: inline-flex;
    align-items: center;
    color: var(--green);
    opacity: 0.45;
    cursor: grab;
    flex-shrink: 0;
    padding: 0 2px;
  }
  .gis-mobile-drag-handle:active { cursor: grabbing; }

  .gis-prop-main {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.4rem;
    padding: 0.5rem 0.55rem;
  }
  .gis-prop-name-wrap { flex: 1 1 120px; min-width: 0; display: flex; flex-direction: column; }
  /* Trailing controls (type, required, settings, remove) grouped so they wrap as a single unit
     onto a second line in a narrow column, rather than each control wrapping independently —
     the name input keeps first claim on line 1. */
  .gis-prop-controls { display: flex; align-items: center; gap: 0.4rem; margin-left: auto; flex-shrink: 0; }
  .gis-prop-type-wrap { flex: 0 0 auto; }
  .gis-prop-type { width: 72px; }
  .gis-prop-align { width: 68px; flex: 0 0 auto; }
  .gis-column-align { width: 68px !important; flex: 0 0 auto; }

  /* Columns render side-by-side now (up to 3 per row) and can be as narrow as their 160px
     flex-basis floor — respond to the column's actual rendered width (not the viewport) so
     the type select shrinks once name + controls no longer fit on one line. */
  .gis-column { container-type: inline-size; }
  @container (max-width: 220px) {
    .gis-prop-type { width: 58px; }
    .gis-prop-align { width: 54px; }
  }

  .gis-dup-warning {
    display: flex;
    align-items: center;
    gap: 3px;
    font-size: 0.65rem;
    font-weight: 600;
    color: #a01233;
    margin-top: 2px;
  }

  /* Inline inputs — strip Bootstrap form-control styles */
  .gis-container .form-control,
  .gis-container .form-control:focus,
  .gis-container .form-control-sm,
  .gis-container .form-control-sm:focus {
    background: transparent !important;
    border: none !important;
    border-bottom: 1px solid rgba(49,59,114,0.15) !important;
    border-radius: 0 !important;
    box-shadow: none !important;
    padding: 1px 2px !important;
    height: auto !important;
    font-size: 0.84rem;
    color: var(--navy);
    transition: border-color 0.12s;
  }
  .gis-container .form-control:focus,
  .gis-container .form-control-sm:focus {
    border-bottom-color: var(--navy) !important;
    outline: none;
  }
  .gis-container .form-control--invalid,
  .gis-container .form-control--invalid:focus {
    border-bottom-color: #a01233 !important;
  }
  .gis-container .form-control::placeholder { color: var(--green); opacity: 0.35; }
  .gis-container select.form-control,
  .gis-container select.form-control-sm {
    padding-right: 14px !important;
    -webkit-appearance: none;
    appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='8' height='5' viewBox='0 0 8 5'%3E%3Cpath d='M0 0l4 5 4-5z' fill='%23313b72' opacity='.35'/%3E%3C/svg%3E") !important;
    background-repeat: no-repeat !important;
    background-position: right 2px center !important;
    cursor: pointer;
  }
  @media (prefers-color-scheme: dark) {
    .gis-container .form-control,
    .gis-container .form-control-sm {
      color: rgba(255,255,255,0.75) !important;
      border-bottom-color: rgba(255,255,255,0.15) !important;
    }
    .gis-container .form-control:focus,
    .gis-container .form-control-sm:focus {
      border-bottom-color: rgba(255,255,255,0.5) !important;
    }
    .gis-container .form-control::placeholder { color: rgba(255,255,255,0.22); }
    .gis-container select.form-control,
    .gis-container select.form-control-sm {
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='8' height='5' viewBox='0 0 8 5'%3E%3Cpath d='M0 0l4 5 4-5z' fill='%23ffffff' opacity='.35'/%3E%3C/svg%3E") !important;
    }
  }

  /* Remove button — matches .structure-delete-btn */
  .gis-x-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    flex-shrink: 0;
    background: transparent;
    border: 1px solid rgba(160,18,51,0.3);
    border-radius: 5px;
    color: #a01233;
    font-size: 0.95rem;
    line-height: 1;
    cursor: pointer;
    transition: background 0.12s, border-color 0.12s, color 0.12s;
    font-family: inherit;
    padding: 0;
  }
  .gis-x-btn:hover { background: #a01233; border-color: #a01233; color: #fff; }
  .gis-x-btn:focus-visible { outline: 2px solid #a01233; outline-offset: 1px; }
  .gis-x-btn--sm { width: 20px; height: 20px; font-size: 0.8rem; }

  /* Required toggle — compact always-visible affordance next to the type select */
  .gis-required-toggle {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    border-radius: 5px;
    border: 1px solid rgba(160,18,51,0.25);
    background: transparent;
    color: rgba(160,18,51,0.45);
    font-size: 0.85rem;
    font-weight: 700;
    line-height: 1;
    cursor: pointer;
    flex-shrink: 0;
    transition: background 0.12s, color 0.12s, border-color 0.12s;
  }
  .gis-required-toggle:hover { border-color: #a01233; color: #a01233; }
  .gis-required-toggle:focus-visible { outline: 2px solid #a01233; outline-offset: 1px; }
  .gis-required-toggle--active { background: #a01233; border-color: #a01233; color: #fff; }

  /* Settings toggle (chevron) — expands the collapsed-by-default settings panel */
  .gis-settings-toggle {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border-radius: 5px;
    border: 1px solid rgba(49,59,114,0.18);
    background: transparent;
    color: var(--navy);
    opacity: 0.55;
    cursor: pointer;
    flex-shrink: 0;
    transition: opacity 0.12s, background 0.12s;
  }
  .gis-settings-toggle:hover { opacity: 1; background: rgba(49,59,114,0.08); }
  .gis-settings-toggle:focus-visible { outline: 2px solid var(--navy); outline-offset: 1px; }
  .gis-settings-toggle--open { opacity: 1; background: rgba(49,59,114,0.08); }
  .gis-settings-toggle svg { transition: transform 0.15s; }
  .gis-settings-toggle--open svg { transform: rotate(180deg); }

  /* Settings panel — collapsed by default; holds everything that isn't needed to just
     scan the property list (Required stays outside, in the always-visible row). */
  .gis-prop-settings {
    padding: 10px 0.6rem 12px 1.9rem;
    border-top: 1px dashed rgba(49,59,114,0.15);
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
  .gis-settings-toggles { display: flex; flex-wrap: wrap; align-items: center; gap: 4px 16px; }
  .gis-flag {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-size: 0.78rem;
    font-weight: 600;
    color: var(--navy);
    cursor: pointer;
    user-select: none;
    padding: 2px 0;
  }
  .gis-flag input[type="checkbox"] { cursor: pointer; margin: 0; }

  .gis-settings-block { display: flex; flex-direction: column; gap: 6px; }
  .gis-settings-block-label {
    font-size: 0.65rem;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--navy);
    opacity: 0.45;
  }
  .gis-settings-row { display: flex; align-items: flex-end; gap: 12px; flex-wrap: wrap; }
  .gis-settings-field { display: flex; flex-direction: column; gap: 3px; }
  .gis-settings-field-label {
    font-size: 0.62rem;
    font-weight: 600;
    color: var(--navy);
    opacity: 0.5;
  }
  .gis-gallery-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 5px 16px;
  }

  /* Mobile props */
  .gis-mobile-prop {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.45rem 0.55rem;
    transition: background 0.12s, opacity 0.12s;
    cursor: default;
  }
  .gis-mobile-prop + .gis-mobile-prop { border-top: 1px solid rgba(56,53,43,0.1); }
  .gis-mobile-prop:hover { background: rgba(49,59,114,0.04); }
  .gis-mobile-prop--hidden { opacity: 0.35; }
  .gis-mobile-prop--dragging { opacity: 0.3; }
  .gis-mobile-prop--over { box-shadow: 0 -2px 0 var(--navy); }
  .gis-mobile-prop-name {
    flex: 1;
    font-size: 0.85rem;
    font-weight: 600;
    color: var(--navy);
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  /* Dark mode */
  @media (prefers-color-scheme: dark) {
    .gis-container { background: var(--dark-surface); }
    .gis-toolbar { background: rgba(255,255,255,0.04); border-bottom-color: rgba(255,255,255,0.07); }
    .gis-tab-btn { background: rgba(255,255,255,0.06); color: rgba(255,255,255,0.6); }
    .gis-tab-btn:hover:not(.gis-tab-btn--active) { background: rgba(255,255,255,0.1); opacity: 1; }
    .gis-add-row-btn { color: rgba(255,255,255,0.6); }
    .gis-row--accent-0 { border-left-color: #9fa8da; }
    .gis-row--accent-1 { border-left-color: #c9a0dc; }
    .gis-row--accent-2 { border-left-color: #6cc9ba; }
    .gis-row--accent-3 { border-left-color: #e0925e; }
    .gis-row--accent-4 { border-left-color: #dfc169; }
    .gis-row--accent-5 { border-left-color: #a8d5b0; }
    .gis-row--over-limit { border-left-color: rgba(224,110,110,0.8); }
    .gis-row-head { background: rgba(255,255,255,0.1); }
    .gis-row-label { color: rgba(255,255,255,0.45); }
    .gis-drag-handle { color: rgba(255,255,255,0.5); }
    .gis-add-col-btn { color: rgba(255,255,255,0.6); }
    .gis-column { background: rgba(255,255,255,0.03); }
    .gis-column-head { background: rgba(255,255,255,0.05); }
    .gis-column-label { color: rgba(255,255,255,0.45); }
    .gis-width-suffix { color: rgba(255,255,255,0.45); }
    .gis-prop-card + .gis-prop-card { border-top-color: rgba(98,168,124,0.3); }
    .gis-prop-card:hover { background: rgba(255,255,255,0.04); }
    .gis-drop-line { background: rgba(255,255,255,0.5); }
    .gis-col-drop-line { background: rgba(255,255,255,0.6); }
    .gis-mobile-drag-handle { color: rgba(255,255,255,0.5); }
    .gis-flag { color: rgba(255,255,255,0.65); }
    .gis-x-btn { border-color: rgba(200,80,80,0.3); color: rgba(200,80,80,0.7); }
    .gis-x-btn:hover { background: #b84040; border-color: #b84040; color: #fff; }
    .gis-required-toggle { border-color: rgba(200,80,80,0.3); color: rgba(200,80,80,0.6); }
    .gis-required-toggle:hover { border-color: #d97a7a; color: #d97a7a; }
    .gis-required-toggle--active { background: #b84040; border-color: #b84040; color: #fff; }
    .gis-settings-toggle { border-color: rgba(255,255,255,0.18); color: rgba(255,255,255,0.75); }
    .gis-settings-toggle:hover, .gis-settings-toggle--open { background: rgba(255,255,255,0.08); }
    .gis-prop-settings { border-top-color: rgba(255,255,255,0.14); }
    .gis-settings-block-label, .gis-settings-field-label { color: rgba(255,255,255,0.55); }
    .gis-mobile-prop + .gis-mobile-prop { border-top-color: rgba(255,255,255,0.07); }
    .gis-mobile-prop:hover { background: rgba(255,255,255,0.04); }
    .gis-mobile-prop--over { box-shadow: 0 -2px 0 rgba(255,255,255,0.4); }
    .gis-mobile-prop-name { color: rgba(255,255,255,0.75); }
    .gis-prop-card--line      { border-left-color: rgba(126,224,129,0.45); }
    .gis-prop-card--text      { border-left-color: rgba(255,255,255,0.2); }
    .gis-prop-card--gallery   { border-left-color: rgba(201,160,220,0.45); }
    .gis-prop-card--poster    { border-left-color: rgba(224,146,94,0.5); }
    .gis-prop-card--date      { border-left-color: rgba(223,193,105,0.45); }
    .gis-prop-card--widget { border-left-color: rgba(108,201,186,0.45); }
    .gis-prop-card--catalog { border-left-color: rgba(224,130,152,0.5); }
    .gis-prop-type-icon--line      { color: #a8d5b0; }
    .gis-prop-type-icon--text      { color: rgba(255,255,255,0.6); }
    .gis-prop-type-icon--gallery   { color: #c9a0dc; }
    .gis-prop-type-icon--poster    { color: #e0925e; }
    .gis-prop-type-icon--date      { color: #dfc169; }
    .gis-prop-type-icon--widget { color: #6cc9ba; }
    .gis-prop-type-icon--catalog { color: #e08298; }
  }
`;

const LayoutStructureEditor = ({ value, onChange, className = '', websiteId }) => {
  const struct = useMemo(() => normalize(value), [value]);
  const [activeTab, setActiveTab] = useState('widescreen');
  const [publicWidgets, setPublicWidgets] = useState([]);

  useEffect(() => {
    if (!websiteId) return;
    let mounted = true;
    get(`websites/${websiteId}/widgets`)
      .then((resp) => { if (mounted) setPublicWidgets(resp?.widgets ?? []); })
      .catch(() => {});
    return () => { mounted = false; };
  }, [websiteId]);

  // Which components currently have their settings panel open — collapsed by default to keep
  // the list scannable; a newly-added property is auto-expanded (see addProperty) since its
  // settings are the first thing you'd want to touch. Keyed by "rIdx:cIdx:pIdx", so reordering
  // a property can leave the wrong card expanded/collapsed until re-toggled — a known, purely
  // cosmetic trade-off for not needing stable per-property ids in the data model.
  const [expandedProps, setExpandedProps] = useState(() => new Set());
  const togglePropSettings = (key) => {
    setExpandedProps((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  // Duplicate property names silently overwrite each other's value once a page fills the
  // layout in (flat {name: value} storage) — flagged live here instead of only at save time.
  const duplicateNames = useMemo(() => {
    const counts = new Map();
    for (const row of struct.rows) {
      for (const col of row.columns) {
        for (const p of col.components) {
          const n = (p.name || '').trim();
          if (!n) continue;
          counts.set(n, (counts.get(n) || 0) + 1);
        }
      }
    }
    const dupes = new Set();
    for (const [n, c] of counts) if (c > 1) dupes.add(n);
    return dupes;
  }, [struct]);

  // Widescreen drag state
  const [propDrag, setPropDrag] = useState(null); // { rIdx, cIdx, pIdx }
  const [propOver, setPropOver] = useState(null); // { rIdx, cIdx, pIdx }
  const propCardRefs = useRef({});
  // "Armed" by mousedown on the drag-handle icon only, and consumed by the next dragstart —
  // so starting a drag from a name input, select, or button inside the card is a no-op instead
  // of accidentally picking the whole card up.
  const propDragArmedRef = useRef(false);

  // Row drag state — rows are draggable as a whole (like prop-cards); the nested prop-card
  // drag handlers below stop propagation so dragging a property never bubbles up into a row drag.
  const [rowDrag, setRowDrag] = useState(-1);
  const [rowOver, setRowOver] = useState(-1);
  const rowRefs = useRef([]);
  const rowDragArmedRef = useRef(false);

  // Column drag state — reordering is scoped to within a single row (columns don't move
  // between rows). { rIdx, cIdx } or null.
  const [colDrag, setColDrag] = useState(null);
  const [colOver, setColOver] = useState(null);
  const colRefs = useRef({});
  const colDragArmedRef = useRef(false);

  // Mobile drag state
  const [mobileDrag, setMobileDrag] = useState(-1);
  const [mobileOver, setMobileOver] = useState(-1);
  const mobileRefs = useRef([]);

  const emit = (nextRows) => {
    const normalized = normalize({ ...struct, rows: nextRows });
    const syncedMobileProps = syncMobileProps(normalized.rows, normalized.mobile.components);
    onChange?.({ ...normalized, mobile: { components: syncedMobileProps } });
  };

  const emitMobile = (nextMobileProps) => {
    onChange?.({ ...struct, mobile: { components: nextMobileProps } });
  };

  const addRow = () => emit([...struct.rows, { columns: [{ ...DEFAULT_COLUMN, components: [] }] }]);
  const removeRow = (rIdx) => emit(struct.rows.filter((_, i) => i !== rIdx));

  const reorderRow = (fromIdx, toIdx) => {
    const rows = [...struct.rows];
    const [moved] = rows.splice(fromIdx, 1);
    rows.splice(toIdx, 0, moved);
    emit(rows);
  };
  const addColumn = (rIdx) => emit(struct.rows.map((row, i) => {
    if (i !== rIdx || row.columns.length >= MAX_COLUMNS) return row;
    const nextCols = [...row.columns, { components: [], width: 100 }];
    const evenWidth = 100 / nextCols.length;
    return { columns: nextCols.map((c) => ({ ...c, width: evenWidth })) };
  }));

  const reorderColumn = (rIdx, fromIdx, toIdx) => emit(struct.rows.map((row, i) => {
    if (i !== rIdx) return row;
    const cols = [...row.columns];
    const [moved] = cols.splice(fromIdx, 1);
    cols.splice(toIdx, 0, moved);
    return { columns: cols };
  }));
  const removeColumn = (rIdx, cIdx) => emit(struct.rows.map((row, i) => {
    if (i !== rIdx) return row;
    const nextCols = row.columns.filter((_, j) => j !== cIdx);
    const evenWidth = nextCols.length > 0 ? 100 / nextCols.length : 100;
    return { columns: nextCols.map((c) => ({ ...c, width: evenWidth })) };
  }));

  const updateColumnWidth = (rIdx, cIdx, width) => emit(struct.rows.map((row, i) => {
    if (i !== rIdx) return row;
    return { columns: row.columns.map((c, j) => (j === cIdx ? { ...c, width } : c)) };
  }));

  const updateColumnAlignment = (rIdx, cIdx, alignment) => emit(struct.rows.map((row, i) => {
    if (i !== rIdx) return row;
    return { columns: row.columns.map((c, j) => (j === cIdx ? { ...c, alignment } : c)) };
  }));

  const addProperty = (rIdx, cIdx) => {
    const newIdx = struct.rows[rIdx]?.columns[cIdx]?.components.length || 0;
    emit(struct.rows.map((row, i) => {
      if (i !== rIdx) return row;
      return { columns: row.columns.map((c, j) => (j === cIdx ? { ...c, components: [...c.components, { ...DEFAULT_PROP }] } : c)) };
    }));
    setExpandedProps((prev) => new Set(prev).add(`${rIdx}:${cIdx}:${newIdx}`));
  };

  const removeProperty = (rIdx, cIdx, pIdx) => emit(struct.rows.map((row, i) => {
    if (i !== rIdx) return row;
    return { columns: row.columns.map((c, j) => (j === cIdx ? { ...c, components: c.components.filter((_, k) => k !== pIdx) } : c)) };
  }));

  const updateProperty = (rIdx, cIdx, pIdx, field, val) =>
    emit(mapAllProperties(struct.rows, (p, ri, ci, pi) => {
      const isTarget = ri === rIdx && ci === cIdx && pi === pIdx;
      if (!isTarget) {
        if (field === 'as_title' && val === true) return { ...p, as_title: false };
        if (field === 'as_subtitle' && val === true) return { ...p, as_subtitle: false };
        return p;
      }
      if (field === 'type') {
        const nextType = String(val || '').toLowerCase();
        if (nextType === 'line') {
          return { ...p, type: 'line', as_title: false, meta: p.meta && typeof p.meta === 'object'
                  ? { prefix: p.meta.prefix || '', suffix: p.meta.suffix || '', fontSize: p.meta.fontSize || 'text' }
                  : { prefix: '', suffix: '', fontSize: 'text' } };
        }
        if (nextType === 'date') {
          return { ...p, type: 'date', meta: null, as_title: false };
        }
        if (nextType === 'widget') {
          return { ...p, type: 'widget', as_title: false, meta: { widgetId: (p.meta && p.meta.widgetId) || null } };
        }
        if (nextType === 'text') {
          return { ...p, type: 'text', as_title: false, meta: { cut: (p.meta && ALLOWED_CUTS.includes(p.meta.cut)) ? p.meta.cut : 'none' } };
        }
        if (nextType === 'gallery') {
          return { ...p, type: 'gallery', as_title: false, meta: normalizeGalleryMeta(p.meta) };
        }
        return { ...p, type: nextType || 'text', meta: null, as_title: false, as_subtitle: false };
      }
      return { ...p, [field]: val };
    }));

  const reorderProperty = (rIdx, cIdx, fromIdx, toIdx) => {
    const props = [...(struct.rows[rIdx]?.columns[cIdx]?.components || [])];
    const [moved] = props.splice(fromIdx, 1);
    props.splice(toIdx, 0, moved);
    emit(struct.rows.map((row, i) => {
      if (i !== rIdx) return row;
      return { columns: row.columns.map((c, j) => (j === cIdx ? { ...c, components: props } : c)) };
    }));
  };
  // Widescreen drag handlers
  // Every handler stops propagation so a property drag never bubbles up and gets
  // misread as a row drag by the row-level handlers below (rows are draggable too).
  const onPropDragStart = (rIdx, cIdx, pIdx, e) => {
    if (!propDragArmedRef.current) { e.preventDefault(); return; }
    e.stopPropagation();
    setPropDrag({ rIdx, cIdx, pIdx });
    setPropOver({ rIdx, cIdx, pIdx });
    try { e.dataTransfer.setData('text/plain', `${rIdx}:${cIdx}:${pIdx}`); } catch (_) {}
    e.dataTransfer.effectAllowed = 'move';
    const el = propCardRefs.current[`${rIdx}:${cIdx}:${pIdx}`];
    if (el && e.dataTransfer.setDragImage) e.dataTransfer.setDragImage(el, 20, 12);
  };
  const onPropDragEnter = (rIdx, cIdx, pIdx, e) => {
    e.stopPropagation();
    if (!propOver || propOver.rIdx !== rIdx || propOver.cIdx !== cIdx || propOver.pIdx !== pIdx) {
      setPropOver({ rIdx, cIdx, pIdx });
    }
  };
  const onPropDragOver = (e) => { e.preventDefault(); e.stopPropagation(); e.dataTransfer.dropEffect = 'move'; };
  const onPropDrop = (rIdx, cIdx, pIdx, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (propDrag && propDrag.rIdx === rIdx && propDrag.cIdx === cIdx && propDrag.pIdx !== pIdx) {
      reorderProperty(rIdx, cIdx, propDrag.pIdx, pIdx);
    }
    setPropDrag(null);
    setPropOver(null);
  };
  const onPropDragEnd = (e) => { e.stopPropagation(); propDragArmedRef.current = false; setPropDrag(null); setPropOver(null); };

  // Row drag handlers — a row is draggable as a whole (mirrors the prop-card pattern), reordered
  // by dropping it onto another row.
  const onRowDragStart = (rIdx, e) => {
    if (!rowDragArmedRef.current) { e.preventDefault(); return; }
    setRowDrag(rIdx);
    setRowOver(rIdx);
    try { e.dataTransfer.setData('text/plain', `row:${rIdx}`); } catch (_) {}
    e.dataTransfer.effectAllowed = 'move';
    const el = rowRefs.current[rIdx];
    if (el && e.dataTransfer.setDragImage) e.dataTransfer.setDragImage(el, 20, 12);
  };
  const onRowDragEnter = (rIdx) => { if (rIdx !== rowOver) setRowOver(rIdx); };
  const onRowDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; };
  const onRowDrop = (rIdx, e) => {
    e.preventDefault();
    if (rowDrag !== -1 && rowDrag !== rIdx) reorderRow(rowDrag, rIdx);
    setRowDrag(-1);
    setRowOver(-1);
  };
  const onRowDragEnd = () => { rowDragArmedRef.current = false; setRowDrag(-1); setRowOver(-1); };

  // Column drag handlers — a column is draggable as a whole (mirrors the row/prop-card pattern).
  // All stop propagation so dragging a column is never misread as dragging its parent row, and
  // reordering is only accepted when dropped within the same row it started in.
  const onColDragStart = (rIdx, cIdx, e) => {
    if (!colDragArmedRef.current) { e.preventDefault(); return; }
    e.stopPropagation();
    setColDrag({ rIdx, cIdx });
    setColOver({ rIdx, cIdx });
    try { e.dataTransfer.setData('text/plain', `col:${rIdx}:${cIdx}`); } catch (_) {}
    e.dataTransfer.effectAllowed = 'move';
    const el = colRefs.current[`${rIdx}:${cIdx}`];
    if (el && e.dataTransfer.setDragImage) e.dataTransfer.setDragImage(el, 20, 12);
  };
  const onColDragEnter = (rIdx, cIdx, e) => {
    e.stopPropagation();
    if (!colDrag || colDrag.rIdx !== rIdx) return;
    if (!colOver || colOver.rIdx !== rIdx || colOver.cIdx !== cIdx) {
      setColOver({ rIdx, cIdx });
    }
  };
  const onColDragOver = (e) => { e.preventDefault(); e.stopPropagation(); e.dataTransfer.dropEffect = 'move'; };
  const onColDrop = (rIdx, cIdx, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (colDrag && colDrag.rIdx === rIdx && colDrag.cIdx !== cIdx) {
      reorderColumn(rIdx, colDrag.cIdx, cIdx);
    }
    setColDrag(null);
    setColOver(null);
  };
  const onColDragEnd = (e) => { e.stopPropagation(); colDragArmedRef.current = false; setColDrag(null); setColOver(null); };

  const displayMobileProps = useMemo(
    () => syncMobileProps(struct.rows, struct.mobile.components),
    [struct.rows, struct.mobile.components]
  );

  const toggleMobileProp = (idx, visible) => {
    emitMobile(displayMobileProps.map((mp, i) => (i === idx ? { ...mp, visible } : mp)));
  };

  const reorderMobileProp = (fromIdx, toIdx) => {
    const props = [...displayMobileProps];
    const [moved] = props.splice(fromIdx, 1);
    props.splice(toIdx, 0, moved);
    emitMobile(props);
  };

  // Mobile drag handlers
  const onMobileDragStart = (idx, e) => {
    setMobileDrag(idx);
    setMobileOver(idx);
    try { e.dataTransfer.setData('text/plain', String(idx)); } catch (_) {}
    e.dataTransfer.effectAllowed = 'move';
    const el = mobileRefs.current[idx];
    if (el && e.dataTransfer.setDragImage) e.dataTransfer.setDragImage(el, 20, 12);
  };
  const onMobileDragEnter = (idx) => { if (idx !== mobileOver) setMobileOver(idx); };
  const onMobileDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; };
  const onMobileDrop = (idx, e) => {
    e.preventDefault();
    if (mobileDrag !== -1 && mobileDrag !== idx) reorderMobileProp(mobileDrag, idx);
    setMobileDrag(-1);
    setMobileOver(-1);
  };
  const onMobileDragEnd = () => { setMobileDrag(-1); setMobileOver(-1); };

  const getMobilePropLabel = (mp) => {
    const prop = struct.rows[mp.row_idx]?.columns[mp.column_idx]?.components[mp.component_idx];
    if (!prop) return '(removed)';
    return prop.name || `Row ${mp.row_idx + 1} / Col ${mp.column_idx + 1} / Prop ${mp.component_idx + 1}`;
  };

  const getMobilePropType = (mp) =>
    struct.rows[mp.row_idx]?.columns[mp.column_idx]?.components[mp.component_idx]?.type || '';

  return (
    <div className={`gis-container${className ? ' ' + className : ''}`}>
      <style>{GIS_STYLES}</style>

      <div className="gis-toolbar">
        <div className="gis-tabs">
          <button type="button" className={`gis-tab-btn${activeTab === 'widescreen' ? ' gis-tab-btn--active' : ''}`} onClick={() => setActiveTab('widescreen')}>Widescreen</button>
          <button type="button" className={`gis-tab-btn${activeTab === 'mobile' ? ' gis-tab-btn--active' : ''}`} onClick={() => setActiveTab('mobile')}>Mobile</button>
        </div>
        {activeTab === 'widescreen' && (
          <button type="button" className="gis-add-row-btn" onClick={addRow}><FiPlus size={12} /> Row</button>
        )}
      </div>

      <div className="gis-content">
        {activeTab === 'widescreen' && (
          <>
            {struct.rows.length === 0 && (
              <div className="gis-empty-hint">No rows yet. Add the first row.</div>
            )}

            {struct.rows.map((row, rIdx) => {
              const total = rowWidthTotal(row);
              const isOverWidth = total > 100.0001;
              const isRowDragging = rowDrag === rIdx;
              const isRowOver = rowOver === rIdx && rowDrag !== -1 && !isRowDragging;
              return (
                <div
                  key={rIdx}
                  ref={(el) => { rowRefs.current[rIdx] = el; }}
                  className={`gis-row gis-row--accent-${rIdx % 6}${isRowDragging ? ' gis-row--dragging' : ''}${isOverWidth ? ' gis-row--over-limit' : ''}`}
                  draggable
                  onDragStart={(e) => onRowDragStart(rIdx, e)}
                  onDragEnter={() => onRowDragEnter(rIdx)}
                  onDragOver={onRowDragOver}
                  onDrop={(e) => onRowDrop(rIdx, e)}
                  onDragEnd={onRowDragEnd}
                >
                  {isRowOver && <div className="gis-drop-line" />}
                  <div className="gis-row-head">
                    {struct.rows.length > 1 && (
                      <span className="gis-reorder-cluster">
                        <span
                          className="gis-drag-handle"
                          aria-hidden="true"
                          onMouseDown={() => { rowDragArmedRef.current = true; }}
                          onMouseUp={() => { rowDragArmedRef.current = false; }}
                        >
                          <FiMenu size={12} />
                        </span>
                      </span>
                    )}
                    <span className="gis-row-label">Row {rIdx + 1}</span>
                    <div className="gis-row-actions">
                      <span className={`gis-row-total${isOverWidth ? ' gis-row-total--over' : ''}`} title={isOverWidth ? 'Column widths exceed 100%' : undefined}>
                        {Math.round(total * 100) / 100}%
                      </span>
                      <button
                        type="button"
                        className="gis-add-col-btn"
                        onClick={() => addColumn(rIdx)}
                        disabled={row.columns.length >= MAX_COLUMNS}
                        title={row.columns.length >= MAX_COLUMNS ? `Maximum ${MAX_COLUMNS} columns` : undefined}
                      >
                        <FiPlus size={11} /> Column
                      </button>
                      <button type="button" className="gis-x-btn gis-x-btn--sm" onClick={() => removeRow(rIdx)} aria-label="Remove row" title="Remove row">×</button>
                    </div>
                  </div>

                  <div className="gis-row-body">
                    {row.columns.length === 0 && (
                      <div className="gis-empty-hint">No columns yet. Add the first column.</div>
                    )}

                    {row.columns.map((col, cIdx) => {
                      const isColDragging = colDrag?.rIdx === rIdx && colDrag?.cIdx === cIdx;
                      const isColOver = colOver?.rIdx === rIdx && colOver?.cIdx === cIdx && colDrag && !isColDragging;
                      const colKey = `${rIdx}:${cIdx}`;
                      return (
                      <div
                        key={cIdx}
                        ref={(el) => { colRefs.current[colKey] = el; }}
                        className={`gis-column${isColDragging ? ' gis-column--dragging' : ''}`}
                        style={{ flex: `${Math.max(Number(col.width) || 0, 10)} 1 160px` }}
                        draggable
                        onDragStart={(e) => onColDragStart(rIdx, cIdx, e)}
                        onDragEnter={(e) => onColDragEnter(rIdx, cIdx, e)}
                        onDragOver={onColDragOver}
                        onDrop={(e) => onColDrop(rIdx, cIdx, e)}
                        onDragEnd={onColDragEnd}
                      >
                        {isColOver && <div className="gis-col-drop-line" />}
                        <div className="gis-column-head">
                          {row.columns.length > 1 && (
                            <span className="gis-reorder-cluster">
                              <span
                                className="gis-drag-handle"
                                aria-hidden="true"
                                onMouseDown={() => { colDragArmedRef.current = true; }}
                                onMouseUp={() => { colDragArmedRef.current = false; }}
                              >
                                <FiMenu size={11} />
                              </span>
                            </span>
                          )}
                          <span className="gis-column-label">Column {cIdx + 1}</span>
                          <div className="gis-column-head-actions">
                            <button type="button" className="gis-add-col-btn" onClick={() => addProperty(rIdx, cIdx)}><FiPlus size={11} /> Property</button>
                            <input
                              type="number"
                              className="form-control form-control-sm gis-width-input"
                              min={1}
                              max={100}
                              step={1}
                              value={Math.round(col.width * 100) / 100}
                              onChange={(e) => updateColumnWidth(rIdx, cIdx, Number(e.target.value) || 0)}
                              aria-label="Column width, percent"
                            />
                            <span className="gis-width-suffix">%</span>
                            <select
                              className="form-control form-control-sm gis-column-align"
                              value={col.alignment || 'top'}
                              onChange={(e) => updateColumnAlignment(rIdx, cIdx, e.target.value)}
                              aria-label="Column vertical alignment"
                              title="Vertical alignment"
                            >
                              <option value="top">top</option>
                              <option value="center">center</option>
                              <option value="bottom">bottom</option>
                            </select>
                            <button type="button" className="gis-x-btn" onClick={() => removeColumn(rIdx, cIdx)} aria-label="Remove column" title="Remove column">×</button>
                          </div>
                        </div>
                        <div className="gis-column-body">
                          {(col.components || []).map((p, pIdx) => {
                            const isDragging = propDrag?.rIdx === rIdx && propDrag?.cIdx === cIdx && propDrag?.pIdx === pIdx;
                            const isOver = propOver?.rIdx === rIdx && propOver?.cIdx === cIdx && propOver?.pIdx === pIdx && propDrag && !isDragging;
                            const cardKey = `${rIdx}:${cIdx}:${pIdx}`;
                            const trimmedName = (p.name || '').trim();
                            const isDuplicate = trimmedName && duplicateNames.has(trimmedName);
                            const isExpanded = expandedProps.has(cardKey);
                            return (
                              <div
                                key={pIdx}
                                ref={(el) => { propCardRefs.current[cardKey] = el; }}
                                className={`gis-prop-card gis-prop-card--${p.type}${isDragging ? ' gis-prop-card--dragging' : ''}${isDuplicate ? ' gis-prop-card--duplicate' : ''}`}
                                draggable
                                onDragStart={(e) => onPropDragStart(rIdx, cIdx, pIdx, e)}
                                onDragEnter={(e) => onPropDragEnter(rIdx, cIdx, pIdx, e)}
                                onDragOver={onPropDragOver}
                                onDrop={(e) => onPropDrop(rIdx, cIdx, pIdx, e)}
                                onDragEnd={onPropDragEnd}
                              >
                                {isOver && <div className="gis-drop-line" />}
                                <div className="gis-prop-main">
                                  {(col.components || []).length > 1 && (
                                    <span className="gis-reorder-cluster">
                                      <span
                                        className="gis-drag-handle"
                                        aria-hidden="true"
                                        onMouseDown={() => { propDragArmedRef.current = true; }}
                                        onMouseUp={() => { propDragArmedRef.current = false; }}
                                      >
                                        <FiMenu size={11} />
                                      </span>
                                    </span>
                                  )}
                                  <span className="gis-prop-name-wrap">
                                    <input
                                      type="text"
                                      className={`form-control form-control-sm${isDuplicate ? ' form-control--invalid' : ''}`}
                                      value={p.name}
                                      onChange={(e) => updateProperty(rIdx, cIdx, pIdx, 'name', e.target.value)}
                                      placeholder="Property name"
                                      aria-label="Property name"
                                    />
                                    {isDuplicate && (
                                      <span className="gis-dup-warning">
                                        <FiAlertTriangle size={10} /> Name used more than once
                                      </span>
                                    )}
                                  </span>
                                  <span className="gis-prop-controls">
                                    <span className="gis-prop-type-wrap">
                                      <span className={`gis-prop-type-icon gis-prop-type-icon--${p.type}`} title={p.type}>
                                        <PropertyTypeIcon type={p.type} />
                                      </span>
                                      <select
                                        className="form-control form-control-sm gis-prop-type"
                                        value={p.type}
                                        onChange={(e) => updateProperty(rIdx, cIdx, pIdx, 'type', e.target.value)}
                                        aria-label="Property type"
                                      >
                                        <option value="line">line</option>
                                        <option value="text">text</option>
                                        <option value="gallery">gallery</option>
                                        <option value="poster">poster</option>
                                        <option value="date">date</option>
                                        <option value="widget">widget</option>
                                        <option value="catalog">catalog</option>
                                        <option value="breadcrumbs">breadcrumbs</option>
                                      </select>
                                    </span>
                                    <select
                                      className="form-control form-control-sm gis-prop-align"
                                      value={p.alignment || 'left'}
                                      onChange={(e) => updateProperty(rIdx, cIdx, pIdx, 'alignment', e.target.value)}
                                      aria-label="Property alignment"
                                      title="Alignment"
                                    >
                                      <option value="left">left</option>
                                      <option value="center">center</option>
                                      <option value="right">right</option>
                                    </select>
                                    <button
                                      type="button"
                                      className={`gis-required-toggle${p.required ? ' gis-required-toggle--active' : ''}`}
                                      onClick={() => updateProperty(rIdx, cIdx, pIdx, 'required', !p.required)}
                                      aria-pressed={!!p.required}
                                      aria-label={p.required ? 'Required — click to make optional' : 'Optional — click to make required'}
                                      title={p.required ? 'Required' : 'Optional'}
                                    >
                                      *
                                    </button>
                                    <button
                                      type="button"
                                      className={`gis-settings-toggle${isExpanded ? ' gis-settings-toggle--open' : ''}`}
                                      onClick={() => togglePropSettings(cardKey)}
                                      aria-expanded={isExpanded}
                                      aria-label={isExpanded ? 'Hide property settings' : 'Show property settings'}
                                      title={isExpanded ? 'Hide settings' : 'Show settings'}
                                    >
                                      <FiChevronDown size={13} />
                                    </button>
                                    <button type="button" className="gis-x-btn" onClick={() => removeProperty(rIdx, cIdx, pIdx)} aria-label="Remove property" title="Remove property">×</button>
                                  </span>
                                </div>

                                {isExpanded && (
                                  <div className="gis-prop-settings">
                                    <div className="gis-settings-toggles">
                                      <label className="gis-flag">
                                        <input type="checkbox" checked={!!p.show_name} onChange={(e) => updateProperty(rIdx, cIdx, pIdx, 'show_name', e.target.checked)} />
                                        Show name on the page
                                      </label>
                                      {p.type === 'line' && (
                                        <label className="gis-flag">
                                          <input type="checkbox" checked={!!p.as_title} onChange={(e) => updateProperty(rIdx, cIdx, pIdx, 'as_title', e.target.checked)} />
                                          Use as title
                                        </label>
                                      )}
                                      {(p.type === 'line' || p.type === 'date') && (
                                        <label className="gis-flag">
                                          <input type="checkbox" checked={!!p.as_subtitle} onChange={(e) => updateProperty(rIdx, cIdx, pIdx, 'as_subtitle', e.target.checked)} />
                                          Use as subtitle
                                        </label>
                                      )}
                                    </div>

                                    {p.type === 'line' && (
                                      <div className="gis-settings-block">
                                        <span className="gis-settings-block-label">Display</span>
                                        <div className="gis-settings-row">
                                          <span className="gis-settings-field">
                                            <span className="gis-settings-field-label">Prefix</span>
                                            <input
                                              type="text"
                                              className="form-control form-control-sm"
                                              style={{ width: 70 }}
                                              value={p.meta?.prefix || ''}
                                              onChange={(e) => updateProperty(rIdx, cIdx, pIdx, 'meta', { ...(p.meta || {}), prefix: e.target.value })}
                                              placeholder="e.g. $"
                                            />
                                          </span>
                                          <span className="gis-settings-field">
                                            <span className="gis-settings-field-label">Suffix</span>
                                            <input
                                              type="text"
                                              className="form-control form-control-sm"
                                              style={{ width: 70 }}
                                              value={p.meta?.suffix || ''}
                                              onChange={(e) => updateProperty(rIdx, cIdx, pIdx, 'meta', { ...(p.meta || {}), suffix: e.target.value })}
                                              placeholder="e.g. cm"
                                            />
                                          </span>
                                          <span className="gis-settings-field">
                                            <span className="gis-settings-field-label">Font size</span>
                                            <select
                                              className="form-control form-control-sm"
                                              style={{ width: 110 }}
                                              value={p.meta?.fontSize || 'text'}
                                              onChange={(e) => updateProperty(rIdx, cIdx, pIdx, 'meta', { ...(p.meta || {}), fontSize: e.target.value })}
                                            >
                                              <option value="heading">heading</option>
                                              <option value="sub-heading">sub-heading</option>
                                              <option value="text">text</option>
                                            </select>
                                          </span>
                                        </div>
                                      </div>
                                    )}
                                    {p.type === 'widget' && (
                                      <div className="gis-settings-block">
                                        <span className="gis-settings-block-label">Default widget</span>
                                        <select
                                          className="form-control form-control-sm"
                                          style={{ width: 240 }}
                                          value={p.meta?.widgetId || ''}
                                          onChange={(e) => updateProperty(rIdx, cIdx, pIdx, 'meta', { widgetId: e.target.value || null })}
                                          aria-label="Default widget"
                                        >
                                          <option value="">{publicWidgets.length === 0 ? 'No widgets available' : 'Select default widget…'}</option>
                                          {publicWidgets.map((c) => (
                                            <option key={c.id} value={c.id}>{c.name} ({c.type})</option>
                                          ))}
                                        </select>
                                      </div>
                                    )}
                                    {p.type === 'catalog' && (
                                      <div className="gis-settings-block">
                                        <span className="muted-hint mb-0">Item layout and elements-in-a-row are set per-page, in that page's own editor — not here, since a shared layout can host this catalog on more than one page.</span>
                                      </div>
                                    )}
                                    {p.type === 'text' && (
                                      <div className="gis-settings-block">
                                        <span className="gis-settings-block-label">Long text</span>
                                        <span className="gis-settings-field">
                                          <span className="gis-settings-field-label">Truncate with "show more"</span>
                                          <select
                                            className="form-control form-control-sm"
                                            style={{ width: 130 }}
                                            value={p.meta?.cut || 'none'}
                                            onChange={(e) => updateProperty(rIdx, cIdx, pIdx, 'meta', { cut: e.target.value })}
                                          >
                                            <option value="none">don't cut</option>
                                            <option value="small">small</option>
                                            <option value="medium">medium</option>
                                          </select>
                                        </span>
                                      </div>
                                    )}
                                    {p.type === 'gallery' && (
                                      <div className="gis-settings-block">
                                        <span className="gis-settings-block-label">Slideshow</span>
                                        <div className="gis-gallery-grid">
                                          <label className="gis-flag">
                                            <input
                                              type="checkbox"
                                              checked={!!p.meta?.autoPlay}
                                              onChange={(e) => updateProperty(rIdx, cIdx, pIdx, 'meta', { ...normalizeGalleryMeta(p.meta), autoPlay: e.target.checked })}
                                            />
                                            Autoplay
                                          </label>
                                          <label className="gis-flag">
                                            <input
                                              type="checkbox"
                                              checked={!!p.meta?.showNav}
                                              onChange={(e) => updateProperty(rIdx, cIdx, pIdx, 'meta', { ...normalizeGalleryMeta(p.meta), showNav: e.target.checked })}
                                            />
                                            Nav arrows
                                          </label>
                                          <label className="gis-flag">
                                            <input
                                              type="checkbox"
                                              checked={!!p.meta?.showFullScreenButton}
                                              onChange={(e) => updateProperty(rIdx, cIdx, pIdx, 'meta', { ...normalizeGalleryMeta(p.meta), showFullScreenButton: e.target.checked })}
                                            />
                                            Full-screen button
                                          </label>
                                          <label className="gis-flag">
                                            <input
                                              type="checkbox"
                                              checked={!!p.meta?.showThumbnails}
                                              onChange={(e) => updateProperty(rIdx, cIdx, pIdx, 'meta', { ...normalizeGalleryMeta(p.meta), showThumbnails: e.target.checked })}
                                            />
                                            Thumbnails
                                          </label>
                                          <label className="gis-flag">
                                            <input
                                              type="checkbox"
                                              checked={!!p.meta?.showImageTitles}
                                              onChange={(e) => updateProperty(rIdx, cIdx, pIdx, 'meta', { ...normalizeGalleryMeta(p.meta), showImageTitles: e.target.checked })}
                                            />
                                            Image titles
                                          </label>
                                        </div>
                                        <span className="gis-settings-field">
                                          <span className="gis-settings-field-label">Slide interval (seconds)</span>
                                          <input
                                            type="number"
                                            className="form-control form-control-sm"
                                            style={{ width: 70 }}
                                            min={1}
                                            value={p.meta?.slideIntervalSeconds ?? 7}
                                            onChange={(e) => updateProperty(rIdx, cIdx, pIdx, 'meta', { ...normalizeGalleryMeta(p.meta), slideIntervalSeconds: Number(e.target.value) || 1 })}
                                          />
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </>
        )}

        {activeTab === 'mobile' && (
          <>
            {displayMobileProps.length === 0 && (
              <div className="gis-empty-hint">No components yet. Add components in the Widescreen tab first.</div>
            )}

            {displayMobileProps.map((mp, idx) => {
              const isDragging = mobileDrag === idx;
              const isOver = mobileOver === idx && mobileDrag !== -1 && !isDragging;
              const type = getMobilePropType(mp);
              return (
                <div
                  key={idx}
                  ref={(el) => { mobileRefs.current[idx] = el; }}
                  className={`gis-mobile-prop${mp.visible ? '' : ' gis-mobile-prop--hidden'}${isDragging ? ' gis-mobile-prop--dragging' : ''}${isOver ? ' gis-mobile-prop--over' : ''}`}
                  draggable
                  onDragStart={(e) => onMobileDragStart(idx, e)}
                  onDragEnter={() => onMobileDragEnter(idx)}
                  onDragOver={onMobileDragOver}
                  onDrop={(e) => onMobileDrop(idx, e)}
                  onDragEnd={onMobileDragEnd}
                >
                  <span className="gis-mobile-drag-handle" aria-hidden="true"><FiMenu size={11} /></span>
                  <input
                    type="checkbox"
                    checked={mp.visible}
                    onChange={(e) => toggleMobileProp(idx, e.target.checked)}
                    title={mp.visible ? 'Hide on mobile' : 'Show on mobile'}
                    aria-label={mp.visible ? `Hide "${getMobilePropLabel(mp)}" on mobile` : `Show "${getMobilePropLabel(mp)}" on mobile`}
                  />
                  <span className="gis-mobile-prop-name">{getMobilePropLabel(mp)}</span>
                  {type && <span className={`item-prop-type-badge item-prop-type-badge--${type}`}>{type}</span>}
                </div>
              );
            })}
          </>
        )}
      </div>
    </div>
  );
};

export default LayoutStructureEditor;
